import { format, subDays, getDay } from 'date-fns';
const moment = require('moment');
import 'moment/locale/de';
import regionsmanagerConfig from '@/core/flugverfuegbarkeit/regionsmanager-reisekuerzel.json';
import store from '@/core/common/services/vuex-store.js';
import { CircleCheck, CircleX } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';

export function isReiseOhneFlug(metadata) {
  return (
    metadata?.flugoption === 'ReiseOhneFlug' &&
    metadata?.transferszenarioHin === 'TX_ohne' &&
    metadata?.transferszenarioRueck === 'TX_ohne'
  );
}

export const fernRegions = ['Asien', 'Amerika', 'Ozeanien', 'Afrika', 'Orient'];
export const euRegions = ['Südeuropa', 'Nordeuropa', 'Dach'];
export const calculateLastSaturday = () => {
  const currentDate = new Date();
  const daysUntilLastSaturday = (getDay(currentDate) + 1) % 7;
  const lastSaturday = subDays(currentDate, daysUntilLastSaturday);
  return format(lastSaturday, 'yyyy-MM-dd');
};

export const formatDate = date => {
  if (date) {
    return format(new Date(date), 'dd.MM.yyyy HH:mm');
  } else return '';
};

export function formatDateWithoutHours(date) {
  if (date) {
    return format(new Date(date), 'dd.MM.yyyy');
  } else return '';
}

// Stellt die Freigaben einer Leistung im historischen Rechnungsnummer-Format dar:
// "RE-1 (100,00 EUR) ; RE-2 (50,00 EUR)" (chronologisch nach Freigabedatum)
export function formatRechnungsnummern(freigaben, waehrung) {
  if (!freigaben || freigaben.length === 0) return '-';
  return [...freigaben]
    .sort((a, b) => new Date(a.freigabedatum) - new Date(b.freigabedatum))
    .map(f => {
      const betrag = (f.freigabebetrag ?? 0).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${f.rechnungsnummer} (${betrag} ${waehrung ?? ''})`;
    })
    .join(' ; ');
}

export function areArraysEqualSets(a1, a2) {
  if (!a1 && !a2) {
    return true;
  }
  if (!a1 || !a2) {
    return false;
  }

  const superSet = {};
  for (const i of a1) {
    const e = i + typeof i;
    superSet[e] = 1;
  }

  for (const i of a2) {
    const e = i + typeof i;
    if (!superSet[e]) {
      return false;
    }
    superSet[e] = 2;
  }

  for (let e in superSet) {
    if (superSet[e] === 1) {
      return false;
    }
  }
  return true;
}

export function isRoleMatchingRouteRoles(userRoles, routeRoles) {
  // check if user has all roles required
  return userRoles.filter(role => routeRoles.includes(role)).length > 0;
}

export function isObjectJSONStringifyEqual(object1, object2) {
  return JSON.stringify(object1) === JSON.stringify(object2);
}

export function tryParseJSONString(str) {
  try {
    const parsedString = JSON.parse(str);
    return { isParsed: true, data: parsedString };
  } catch (e) {
    return { isParsed: false, data: str };
  }
}

export function isOptionsRefreshableByValueAndKey(value, key, currentFilterOptions, startSearchAtLength) {
  if (!currentFilterOptions) {
    return true;
  }
  const currentObjectInArrayWithSameId = currentFilterOptions.filter(object =>
    object[key].toString().includes(value)
  );
  return (
    value && value.toString().length > startSearchAtLength - 1 && currentObjectInArrayWithSameId.length >= 0
  );
}

export function buildKuerzelFilterOptionsArray(reisenFilterOptionsArray, kuerzelFilterOptionsArray) {
  const transformedReisekuerzelFilter =
    reisenFilterOptionsArray &&
    reisenFilterOptionsArray.map(filter => ({
      kuerzelId: filter.id,
      kuerzelLabel: filter.reisekuerzel,
      isReisekuerzel: true,
    }));

  const transformedReiseterminkuerzelFilter =
    kuerzelFilterOptionsArray &&
    kuerzelFilterOptionsArray.map(filter => ({
      kuerzelId: filter.id,
      kuerzelLabel: filter.reiseterminkuerzel,
      isReisekuerzel: false,
    }));

  if (
    Array.isArray(transformedReiseterminkuerzelFilter) &&
    transformedReiseterminkuerzelFilter.length !== 0 &&
    Array.isArray(transformedReisekuerzelFilter) &&
    transformedReisekuerzelFilter.length !== 0
  ) {
    return transformedReisekuerzelFilter.concat(transformedReiseterminkuerzelFilter);
  } else if (
    Array.isArray(transformedReiseterminkuerzelFilter) &&
    transformedReiseterminkuerzelFilter.length !== 0
  ) {
    return transformedReiseterminkuerzelFilter;
  } else if (Array.isArray(transformedReisekuerzelFilter) && transformedReisekuerzelFilter.length !== 0) {
    return transformedReisekuerzelFilter;
  } else {
    return [];
  }
}

export function isIdFilterRefreshable(id, currentFilterOptions, startSearchAtLength = 3) {
  // if id is empty set the options to an empty array
  // if the filter is greater than 2 digits start the search
  // if the search id is already in the options array do not search
  const currentObjectInArrayWithSameId = currentFilterOptions.filter(object =>
    object.id.toString().includes(id)
  );
  return id && id.toString().length > startSearchAtLength - 1 && currentObjectInArrayWithSameId.length >= 0;
}

export function parseFullUsername(user) {
  if (!user) return '';
  if (user.displayName) return user.displayName;
  const surname = user.surname ? ' ' + user.surname : ''; // prepend empty space as a workaround to filter user without surname correctly
  return `${user.givenName}${surname}`;
}

export function isUsernameMatching(user, username) {
  // compare usernames case insensitve (... will even filter if username is manual entry matches)
  return parseFullUsername(user).localeCompare(username, undefined, { sensitivity: 'base' }) === 0
    ? true
    : false;
}

export function getConfigStringsByObjectType(typeString) {
  return {
    stateObjName: 'reiseterminState',
    localeKey: typeString === 'reisetermin' || typeString === 'reisetermine' ? 'REISETERMINE' : 'VORGAENGE',
    route: typeString === 'reisetermin' || typeString === 'reisetermine' ? 'reisetermin' : 'vorgang',
    configString:
      typeString === 'reisetermin' || typeString === 'reisetermine' ? 'reisetermine' : 'vorgaenge',
  };
}

export function groupBy(data, key) {
  // groups an array of objects by the given key
  // key is case sensitive
  return data.reduce((accumulator, currentItem) => {
    let group = currentItem[key];
    accumulator[group] = accumulator[group] || [];
    accumulator[group].push(currentItem);
    return accumulator;
  }, {});
}

export function sortFluegeByAbflugdatumAndAbflugzeit(fluege) {
  // Sorts fluege by abflugdatum. If they are the same fall back to abflugzeit
  return fluege.sort((a, b) => {
    return (
      new Date(a.abflugdatum).valueOf() - new Date(b.abflugdatum).valueOf() || a.abflugzeit - b.abflugzeit
    );
  });
}

export function getDifferentFlights(newFlights, oldFlights) {
  // if new and old flights are there
  // return all old flights that have different abflugdatum or ankunftsdatum than
  // all new flights
  return (
    newFlights &&
    oldFlights &&
    oldFlights.filter(oldFlight => {
      return !newFlights.some(newFlight => {
        return (
          oldFlight.abflugdatum === newFlight.abflugdatum &&
          oldFlight.ankunftsdatum === newFlight.ankunftsdatum
        );
      });
    })
  );
}

export function parseEigenanreiseText(vorgang) {
  // Tries to parse eigenanreiseText
  const eigenanreiseText = vorgang.eigenanreiseText;
  const eigenanreiseRegEx =
    /(?<type>Ankunft|Ankunftszeit|Abflug|Abflugzeit):\s*(?<datum>(\d{2}).(\d{2}).(?!Uhr))*\s*(?<zeit>(2[0-4]|[0-1]?[\d])[:|.]([0-5][\d]))*\s*(Uhr)*[,|\\.]{0,1}\s*(?:(ab|in):\s*(?<flughafen>[A-Z]{3}))*\s*(Flugnummer|Flugnr)*\s*:\s*(?<flugnummer>(?<![\dA-Z])(?!\d{1,4})\s*([A-Z\d]{1,4})\s*?(\d{1,4})(?!\d))*/gm;
  const firstMatch = eigenanreiseRegEx.exec(eigenanreiseText);
  const secondMatch = eigenanreiseRegEx.exec(eigenanreiseText);
  const parsedObject = firstMatch
    ? secondMatch
      ? {
          [firstMatch.groups.type]: { ...firstMatch.groups },
          [secondMatch.groups.type]: { ...secondMatch.groups },
        }
      : { [firstMatch.groups.type]: { ...firstMatch.groups } }
    : null;
  return parsedObject;
}

export const changePeopleToCorrectShape = data => {
  return data
    .filter(({ givenName }) => givenName !== null && !givenName.toLowerCase().includes('test'))
    .filter(({ surname }) => surname !== null && !surname.toLowerCase().includes('test'))
    .filter(({ mail }) => mail !== null)
    .map(user => ({
      value: user.id,
      text: parseFullUsername(user),
    }));
};

export const teamAbkuerzungen = {
  'Destination Operations Management': 'DOM',
  'Destination Success Management': 'DSM',
  'Customer Success Management': 'CSM',
  'Flight Operations Management': 'FOM',
  'Flight Operations Management GDS': 'FOM GDS',
  'Flight Operations Management NDC': 'FOM NDC',
};

export const getUserTeams = user => {
  // Es wird immer das Department genommen, außer bei Katalog Sales
  // Dort wird das Team genommen
  if (user?.department === 'Katalog Sales' && Array.isArray(user?.teams) && user.teams.length > 0) {
    return user.teams.filter(t => !!t);
  }
  // Bei Flight Operations Management werden nur die Teams GDS und NDC übernommen
  // Ohne eines der beiden Teams greift unten der Fallback auf das Department
  if (user?.department === 'Flight Operations Management' && Array.isArray(user?.teams)) {
    const fomTeams = user.teams.filter(
      t => t === 'Flight Operations Management GDS' || t === 'Flight Operations Management NDC'
    );
    if (fomTeams.length > 0) return fomTeams;
  }
  return user?.department ? [user.department] : [];
};

// Nur echte Teams unterhalb der Abteilung, z. B. FOM GDS; ohne eigenes Team (z. B. DOM) bleibt die Liste leer
export const getUserTeamsOhneAbteilung = user => getUserTeams(user).filter(t => t !== user?.department);

export const changeTeamsToCorrectShape = data => {
  const teamNames = data
    .filter(({ givenName }) => givenName !== null && !givenName.toLowerCase().includes('test'))
    .filter(({ surname }) => surname !== null && !surname.toLowerCase().includes('test'))
    .filter(({ mail }) => mail !== null)
    // Department zusätzlich, damit z. B. FOM neben FOM GDS/NDC auswählbar bleibt
    .flatMap(user => [user.department, ...getUserTeams(user)]);
  return (
    [...new Set(teamNames)]
      // Katalog Sales und Customer Success Management werden aus dem Dropdown rausgefiltert
      .filter(t => t != null && t !== '' && t !== 'Katalog Sales' && t !== 'Customer Success Management')
      .map(t => ({ value: t, text: t }))
      .sort((a, b) => a.text.localeCompare(b.text))
  );
};

export const getRegionsmanagerByShortname = shortname => {
  const rm = Object.entries(regionsmanagerConfig).find(([key, value]) => value.shortname === shortname);
  return rm ?? null;
};

export const getRegionsmanagerOfReisekuerzel = reisekuerzel => {
  const regionsmanagerConfigStore = store.getters.getRegionsmanagerConfig;
  const responsibleRegionsmanager = Object.entries(regionsmanagerConfigStore).find(([key, value]) =>
    value.includes(reisekuerzel)
  )?.[0];
  const responsibleRegionsmanagerName = regionsmanagerConfig[responsibleRegionsmanager]?.shortname;
  return responsibleRegionsmanagerName;
};

export const getReisenOfRegionsmanager = shortname => {
  const regionsmanagerConfigStore = store.getters.getRegionsmanagerConfig;
  const regionsmanagerFromConfig = getRegionsmanagerByShortname(shortname);
  const idRegionsmanager = regionsmanagerFromConfig?.[0];
  const reisen = regionsmanagerConfigStore[idRegionsmanager];
  return reisen;
};

export const getRegionsmanagerById = id => {
  const rm = regionsmanagerConfig[id];
  return rm ?? null;
};

export const triggerVariant = trigger => {
  const triggerFormatter = {
    TriggerInArbeit: 'warning',
    TriggerOffen: 'danger',
    TriggerErledigt: 'success',
    TriggerPending: 'info',
  };
  return triggerFormatter[trigger.trigger];
};

export const triggerLabel = (triggerName, isErgebnisFesthalten = null) => {
  if (isErgebnisFesthalten && triggerName === 'TriggerErledigt') return 'Ergebnis festhalten und erledigen';
  const triggerLabels = {
    TriggerInArbeit: 'In Arbeit',
    TriggerOffen: 'Offen',
    TriggerErledigt: 'Erledigt',
    TriggerPending: 'Pending',
  };
  return triggerLabels[triggerName];
};

export const getFlughaefenString = (item, key) => {
  if (!item || !item[key] || item[key].length === 0) {
    return '-';
  }
  if (item[key] && typeof item[key][0] === 'string') {
    return item[key].join(', ');
  } else {
    return (item[key] ?? []).map(flughafen => flughafen.flughafenIataCode).join(', ');
  }
};

export const permittedDepartments = [
  'Yield Fulfillment',
  'Yield Controlling',
  'Product Management',
  'Destination Operations Management',
  'Flight Operations Management',
  'Customer Success Management',
];

export const releaseprozessIconsColors = value => {
  return value === 'AllGood' ? 'text-success' : value === 'CheckRequired' ? 'text-warning' : 'text-danger';
};

export const kategorienSortRanking = {
  TempPruefungDurchfuehrung: 0,
  ReleasepruefungVorgange: 1,
  ReleasepruefungTermin: 2,
  NeueBuchung: 3,
  Transferplanung: 4,
  StornoOderFlugzeitenAenderung: 5,
};

export const checkDateInFuture = date => {
  if (date) {
    const selectedMoment = moment(date, 'YYYY-MM-DD');
    const today = moment().subtract(1, 'days');

    return selectedMoment.isAfter(today, 'day');
  } else {
    return false;
  }
};

export const exactlySixChars = value => {
  return value !== null && value.length === 6;
};

export const noDigits = value => {
  return value !== null && !/\d/.test(value);
};

export const transferszenarioOptions = [
  { text: 'TX', value: 'TX_ohne' },
  { text: 'T1', value: 'T1_1inkl' },
  { text: 'T2', value: 'T2_1inkl_exkl_mögl' },
  { text: 'T3', value: 'T3_unbegr_inkl' },
  { text: 'T4', value: 'T4_unbegr_exkl' },
  { text: 'T5', value: 'T5_fest_inkl' },
  { text: 'T6', value: 'T6' },
  { text: 'T5_2', value: 'T5_2' },
  { text: 'T5_3', value: 'T5_3' },
  { text: 'TS', value: 'TS' },
];

export const minimaltransferszenarioOptions = [
  { text: 'TX', value: 'TX_ohne' },
  { text: 'T3', value: 'T3_unbegr_inkl' },
  { text: 'T4', value: 'T4_unbegr_exkl' },
  { text: 'TS', value: 'TS' },
];

export const langConfig = {
  formatLocale: {
    months: moment.localeData('de').months(),
    monthsShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    weekdays: moment.localeData('de').weekdays(),
    weekdaysShort: moment.localeData('de').weekdaysShort(),
    weekdaysMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    firstDayOfWeek: 1,
  },
};

// GRP Konzept auswählbare Einkaufsarten
export const einkaufsartSelectableOptions = [
  { text: '-', value: null },
  { text: 'RES', value: 'Res' },
  { text: 'FLEX', value: 'Flex' },
  { text: 'EP', value: 'Ep' },
];

// Alte Einkaufsarten - nicht mehr auswählbar, nur noch für Anzeige & Filter bestehender Datensätze
export const einkaufsartLegacyOptions = [
  { text: 'Backup geplant', value: 'BackupGeplant' },
  { text: 'Backup bestätigt', value: 'BackupBestaetigt' },
  { text: 'EP BU zurück', value: 'EpBackupZurueck' },
  { text: 'EP BU nicht bestätigt', value: 'EpBackupNichtBestaetigt' },
  { text: 'RES geplant', value: 'RestriktivGeplant' },
  { text: 'RES bestätigt/buchbar', value: 'RestriktivBestaetigtBuchbar' },
  { text: 'EP RES zurück', value: 'EpResZurueck' },
  { text: 'EP RES nicht bestätigt', value: 'EpResNichtBestaetigt' },
  { text: 'RES statt BU', value: 'RestriktivStattBackup' },
  { text: 'Flex geplant', value: 'FlexGeplant' },
  { text: 'Flex buchbar', value: 'FlexBuchbar' },
  { text: 'Flex bestätigt', value: 'FlexBestaetigt' },
  { text: 'EP Flex nicht bestätigt', value: 'EpFlexNichtBestaetigt' },
  { text: 'EP Flex zurück', value: 'EpFlexZurueck' },
  { text: 'Leerverkauf Flex', value: 'LeerverkaufFlex' },
  { text: 'Leerverkauf RES', value: 'LeerverkaufRes' },
  { text: 'Leerverkauf kurzes TKTL', value: 'LeerverkaufKurzesTktl' },
];

// Vollständige Liste
export const einkaufsartOptions = [...einkaufsartSelectableOptions, ...einkaufsartLegacyOptions];

// GRP Konzept auswählbare Statusgruppen
export const statusgruppeSelectableOptions = [
  { text: '-', value: null },
  { text: 'Geplant', value: 'Geplant' },
  { text: 'Angefragt', value: 'Angefragt' },
  { text: 'Quotiert', value: 'Quotiert' },
  { text: 'Angelegt', value: 'Angelegt' },
  { text: 'Angelegt Leerverkauf', value: 'AngelegtLeerverkauf' },
  { text: 'Sammelquotierung Prüfung', value: 'SammelquotierungPruefung' },
  { text: 'Sammelquotierung angelegt', value: 'SammelquotierungAngelegt' },
  { text: 'Sammelquotierung Leerverkauf', value: 'SammelquotierungLeerverkauf' },
  { text: 'Backup', value: 'Backup' },
  { text: 'EP nach GRP', value: 'EpNachGrp' },
  { text: 'EP', value: 'Ep' },
];

// Alte Statusgruppen - nicht mehr auswählbar, nur noch für Anzeige & Filter bestehender Datensätze
export const statusgruppeLegacyOptions = [
  { text: 'Gruppe geplant', value: 'GruppeGeplant' },
  { text: 'Checked ok', value: 'CheckedOk' },
  { text: 'Checked ok teurer Kalk', value: 'CheckedOkTeurerKalk' },
  { text: 'Angelegt, Anpassung nötig', value: 'AngelegtAnpassungNoetig' },
  { text: 'EP nach GRP Opt', value: 'EpNachGruppeOptional' },
  { text: 'EP nach GRP sonstiges', value: 'EpNachGruppeSonstiges' },
];

// Vollständige Liste
export const statusgruppeOptions = [...statusgruppeSelectableOptions, ...statusgruppeLegacyOptions];

// Mapping-Tabelle -> Kombination aus Einkaufsart + Statusgruppe zur Badge-Anzeige (GRP-Konzept ab 2026)
const einkaufsartAnzeigeMap = {
  Ep: { _default: 'EP' },
  Flex: {
    Geplant: 'EP',
    Angefragt: 'EP',
    Quotiert: 'EP',
    SammelquotierungPruefung: 'EP',
    Backup: 'EP',
    EpNachGrp: 'EP',
    Angelegt: 'Flex',
    AngelegtLeerverkauf: 'Flex',
    SammelquotierungAngelegt: 'Flex',
    SammelquotierungLeerverkauf: 'Flex',
  },
  Res: {
    Geplant: 'EP nur ',
    Angefragt: 'EP nur ',
    Quotiert: 'EP nur ',
    SammelquotierungPruefung: 'EP nur ',
    Backup: 'EP nur ',
    EpNachGrp: 'EP',
    Angelegt: 'RES',
    AngelegtLeerverkauf: 'RES',
    SammelquotierungAngelegt: 'RES',
    SammelquotierungLeerverkauf: 'RES',
  },
};

export function getEinkaufsartAnzeige(einkaufsart, statusgruppe, airlineCode) {
  if (einkaufsart === 'Ep') return 'EP';
  if ((einkaufsart === 'Flex' || einkaufsart === 'Res') && statusgruppe == null) return '-';
  const label = einkaufsartAnzeigeMap[einkaufsart]?.[statusgruppe] ?? 'EP';
  if (label === 'EP nur ') return airlineCode ? `EP nur ${airlineCode}` : 'EP';
  return label;
}

// Mapping-Tabelle -> Kombination aus Einkaufsart + Statusgruppe zur Anzeige als GRP Status im FVC
const grpStatusFvcSuffixe = {
  Geplant: 'geplant',
  Angefragt: 'geplant',
  Quotiert: 'geplant',
  SammelquotierungPruefung: 'geplant',
  Angelegt: 'angelegt',
  SammelquotierungAngelegt: 'angelegt',
  AngelegtLeerverkauf: 'leer',
  SammelquotierungLeerverkauf: 'leer',
  Backup: 'Backup',
};

// EP nach GRP wird unabhängig von der Einkaufsart gleich angezeigt
const buildGrpStatusFvcEintraege = prefix => ({
  ...Object.fromEntries(
    Object.entries(grpStatusFvcSuffixe).map(([statusgruppe, suffix]) => [statusgruppe, `${prefix}_${suffix}`])
  ),
  EpNachGrp: 'EP nach GRP',
});

export const grpStatusFvcMap = {
  Flex: buildGrpStatusFvcEintraege('FLEX'),
  Res: buildGrpStatusFvcEintraege('RES'),
};

// Werte für den Set-Filter der GRP-Status-Spalte, '-' steht für nicht zuordenbare Kombinationen
export const grpStatusFvcFilterValues = [
  'EP',
  'EP nach GRP',
  ...['FLEX', 'RES'].flatMap(prefix => ['geplant', 'angelegt', 'leer', 'Backup'].map(s => `${prefix}_${s}`)),
  '-',
];

// Liefert den GRP Status zur Kombination aus Einkaufsart und Statusgruppe
export function getGrpStatusFvc(einkaufsart, statusgruppe) {
  if (einkaufsart === 'Ep') return 'EP';
  return grpStatusFvcMap[einkaufsart]?.[statusgruppe] ?? '-';
}

// Prüft, ob die Kombination aus Einkaufsart und Statusgruppe unzulässig ist
export function isEinkaufsartStatusgruppeVerboten(einkaufsart, statusgruppe) {
  if (einkaufsart === 'Ep' && statusgruppe != null && statusgruppe !== 'Ep') return true;
  if (statusgruppe === 'Ep' && einkaufsart != null && einkaufsart !== 'Ep') return true;
  return false;
}

export const grundOptions = [
  { text: 'Keine Bestätigung CM', value: 'KeineBestaetigungCm' },
  { text: 'Keine Flugverfügbarkeit', value: 'KeineFlugverfuegbarkeit' },
  { text: 'Saisonales Ereignis', value: 'SaisonalesEreignis' },
  { text: 'Verschiebung aufgrund von Planüberprüfung', value: 'VerschiebungPlanueberpruefung' },
  { text: 'Sonstiges', value: 'Sonstiges' },
];

export const verworfenGrundToLabel = grundOptions.reduce((acc, curr) => {
  acc[curr.value] = curr.text;
  return acc;
}, {});

function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

export function arraysEqualUnordered(arr1, arr2) {
  if (
    typeof arr1 === 'string' ||
    typeof arr2 === 'string' ||
    typeof arr1 === 'number' ||
    typeof arr2 === 'number'
  )
    return false;
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (!deepEqual(sortedArr1[i], sortedArr2[i])) return false;
  }

  return true;
}

export function arraysEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (var i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// Vergleicht zwei Zielland-Flughafenlisten reihenfolgeunabhängig; zwei null-Werte gelten als gleich
export function areZiellandFlughaefenEqual(ziellandFlughaefen, ziellandFlughaefenSeason) {
  if (ziellandFlughaefen === null && ziellandFlughaefenSeason === null) return true;

  if (Array.isArray(ziellandFlughaefen) && Array.isArray(ziellandFlughaefenSeason)) {
    return arraysEqual(ziellandFlughaefen.slice().sort(), ziellandFlughaefenSeason.slice().sort());
  }

  return false;
}

// Entfernt eckige Klammern aus einem JSON-String, damit Arrays vergleichbar werden
export function stripBrackets(value) {
  return value?.replaceAll('[', '').replaceAll(']', '');
}

// Prüft, ob ein Feldwert unverändert ist
export function areValuesEqual(value, valueEdited) {
  return (
    value === valueEdited ||
    stripBrackets(JSON.stringify(value)) === stripBrackets(JSON.stringify(valueEdited))
  );
}

export function getBlankSyncState(enumValue) {
  const translation = {
    AvailableInBlank: 'Angelegt',
    NotAvailableInBlank: 'Nicht angelegt',
    None: 'Nicht synchronisiert',
    AvailableInBlankAndSyncedHotelleistungWithRelations: 'Angelegt',
    AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingente: 'Angelegt',
    AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingenteAndZusatzleistungen: 'Angelegt',
  };

  return translation[enumValue];
}

export function getInBlankAngelegt(reiseterminSyncState) {
  if (reiseterminSyncState === 'None') {
    return 'Nicht synchronisiert';
  } else if (reiseterminSyncState === 'NotAvailableInBlank') {
    return 'Nicht in Blank angelegt';
  }

  return 'In Blank angelegt';
}

export const flugstammdatenAufgabenKategorien = [
  'AnlageFlugstamm',
  'ThVkAenderung',
  'KalkulationstabellePruefen',
  'FhAenderung',
  'ResAenderung',
  'TrfSzenarioAenderung',
  'Terminverschiebung',
  'NPruefung',
];

export const flugplanungAufgabenKategorien = ['OptionsfristErreicht'];

// Liefert Icon-Komponente und Farbklasse zum Vorzeitig-garantiert-Status
const vorzeitigGarantiertConverter = {
  VorzeitigGarantiert: { icon: CircleCheck, class: 'text-success' },
  NichtVorzeitigGarantiert: { icon: CircleX, class: 'text-danger' },
  WarVorzeitigGarantiert: { icon: CircleCheck, class: 'text-danger' },
};

export function vorzeitigGarantiertIcon(vorzeitigGarantiert) {
  return vorzeitigGarantiertConverter[vorzeitigGarantiert] ?? null;
}

// Liefert das Vorzeitig-garantiert-Icon als HTML-String für AG-Grid-Renderer
export function vorzeitigGarantiertHtml(vorzeitigGarantiert) {
  const config = vorzeitigGarantiertIcon(vorzeitigGarantiert);
  return config ? lucideHtml(config.icon, { size: 20, class: config.class }) : null;
}

export const maxWartezeitOptions = [
  0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 390, 420, 450, 480, 510, 540, 570, 600, 630,
  660, 690, 720,
];

export const hotelwechselOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const DepartmentButtonClass = {
  'Product Management': {
    label: 'PM',
    color: '#1BC5BD',
    fontColor: 'white',
  },
  'Yield Fulfillment': {
    label: 'YF',
    color: '#FFA800',
    fontColor: 'white',
  },
  'Destination Operations Management': {
    label: 'DOM',
    color: '#F64E60',
    fontColor: 'white',
  },
  'Flight Operations Management': {
    label: 'FOM',
    color: '#3699FF',
    fontColor: 'white',
  },
  'Customer Management': {
    label: 'CM',
    color: '#8950FC',
    fontColor: 'white',
  },
  Tech: {
    label: 'Tech',
    color: '#212121',
    fontColor: 'white',
  },
  'Yield Controlling': {
    label: 'YC',
    color: '#C8A2C8',
    fontColor: 'white',
  },
  'Destination Success Management': {
    label: 'DSM',
    color: '#006400',
    fontColor: 'white',
  },
  'Katalog Sales': {
    label: 'S',
    color: '#ff7f24',
    fontColor: 'white',
  },
  Buchhaltung: {
    label: 'BH',
    color: '#607D8B',
    fontColor: 'white',
  },
  Controlling: {
    label: 'CON',
    color: '#00897B',
    fontColor: 'white',
  },
  IT: {
    label: 'IT',
    color: '#37474F',
    fontColor: 'white',
  },
  Marketing: {
    label: 'MK',
    color: '#E91E63',
    fontColor: 'white',
  },
  'Office Management': {
    label: 'OM',
    color: '#78909C',
    fontColor: 'white',
  },
  'People & Culture': {
    label: 'PC',
    color: '#AB47BC',
    fontColor: 'white',
  },
  'Project Management': {
    label: 'PJM',
    color: '#5C6BC0',
    fontColor: 'white',
  },
  'Sales Privatreisen': {
    label: 'SP',
    color: '#EF6C00',
    fontColor: 'white',
  },
  Vertrieb: {
    label: 'VT',
    color: '#2E7D32',
    fontColor: 'white',
  },
};

export const customerManagementTeams = ['Customer Operations Management', 'Customer Success Management'];

export function departmentButtonClass(department) {
  // Die Customer-Teams werden auf den gemeinsamen Customer-Management-Button abgebildet
  if (customerManagementTeams.includes(department)) {
    return DepartmentButtonClass['Customer Management'];
  }
  return DepartmentButtonClass[department] || {};
}

export function getDepartmentNameByLabel(departmentLabel) {
  return Object.keys(DepartmentButtonClass).find(key => DepartmentButtonClass[key].label === departmentLabel);
}

export function getDepartmentNamesByLabel(departmentLabel) {
  const alleDepartmentNamen = [...Object.keys(DepartmentButtonClass), ...customerManagementTeams];
  return alleDepartmentNamen.filter(name => departmentButtonClass(name).label === departmentLabel);
}

export function filterCommentsByDepartment(comments, selectedDepartment, users) {
  if (!selectedDepartment) {
    return comments ?? [];
  }

  const departmentNames = getDepartmentNamesByLabel(selectedDepartment);

  if (departmentNames.length === 0) return comments ?? [];

  const departmentUserIds = users
    .filter(user => departmentNames.includes(user.department))
    .map(user => user.id);

  return comments?.filter(comment => departmentUserIds.includes(comment.creator)) ?? [];
}

export function isUserInDepartmentByLabel(userId, departmentLabel, users) {
  const departmentNames = getDepartmentNamesByLabel(departmentLabel);
  if (departmentNames.length === 0) return false;
  return users.some(user => user.id === userId && departmentNames.includes(user.department));
}

export function formatAndCalculateReleasedatum(datum, releasefrist) {
  const startdatum = new Date(datum);
  const generatedDatum = subDays(startdatum, releasefrist);
  return format(generatedDatum, 'dd.MM.yyyy');
}

export function translateStatusGruppeOptions(value) {
  return statusgruppeOptions.find(option => option.value === value)?.text ?? '-';
}

export function translateEinkaufsartOptions(value) {
  return einkaufsartOptions.find(option => option.value === value)?.text ?? '-';
}

// Farbwerte für die Status-Pills (Hintergrund, Rahmen, Text)
export const aufgabenStatusColors = {
  Offen: { background: '#F64E6026', borderColor: '#F64E60', color: '#C10007' },
  InArbeit: { background: '#FFA80026', borderColor: '#FFA800', color: '#D97706' },
  Pending: { background: '#7337EE26', borderColor: '#7337EE', color: '#3F168F' },
  Erledigt: { background: '#1BC5BD26', borderColor: '#1BC5BD', color: '#00786F' },
};

// Neutrale Pill-Farben, z.B. für nicht überfällige Fälligkeitsdaten oder inaktive Buttons
export const grauPillColors = { background: '#B5B5C34D', borderColor: '#B5B5C3', color: '#181D1F' };

// Farbwerte der Vorgangs-Status-Pills, nicht aufgeführte Status werden grau dargestellt
export const vorgangStateColors = {
  Buchung: aufgabenStatusColors.Erledigt,
  StornierteBuchung: aufgabenStatusColors.Offen,
  StornierteOption: aufgabenStatusColors.Offen,
  StornierteAnfrage: aufgabenStatusColors.Offen,
  StornierteWarteliste: aufgabenStatusColors.Offen,
  StornierteVormerkung: aufgabenStatusColors.Offen,
  StorniertOhneRechnung: aufgabenStatusColors.Offen,
};

// Liefert die Pill-Farben als Inline-Style-String (Layout kommt aus der CSS-Klasse pill-badge)
export function pillColorStyle({ background, borderColor, color }) {
  return `background: ${background}; border: 0.5px solid ${borderColor}; color: ${color}`;
}

// Liefert die Pill-Farben als Style-Objekt für Vue-Style-Bindings
export function pillColorStyleObject({ background, borderColor, color }) {
  return { background, border: `0.5px solid ${borderColor}`, color };
}

// Leitet aus den Pill-Farben den deutlich schwächeren Hintergrund eines Banners ab
export function bannerBgColor({ borderColor }) {
  return `${borderColor}0D`;
}

// Erzeugt das HTML einer Pill im neuen Design mit den übergebenen Farben
// Die Small-Variante hat weniger vertikales Padding, damit sie in die festen Zeilenhöhen der Grid-Filter passt
function pillHtml(colors, label, { small = false, muted = false, widthClass = '' } = {}) {
  const classes = ['pill-badge'];
  if (small) classes.push('pill-badge--small');
  if (muted) classes.push('pill-badge--muted');
  if (widthClass) classes.push(widthClass);
  return `<span class="${classes.join(' ')}" style="${pillColorStyle(colors)}">${label}</span>`;
}

// Erzeugt das HTML einer Status-Pill im neuen Design
function statusPillHtml(status, label, options) {
  return pillHtml(aufgabenStatusColors[status], label, { ...options, widthClass: 'pill-badge--status' });
}

export const statusFormatterHtml = {
  InArbeit: statusPillHtml('InArbeit', 'In Arbeit'),
  Erledigt: statusPillHtml('Erledigt', 'Erledigt'),
  Pending: statusPillHtml('Pending', 'Pending'),
  Offen: statusPillHtml('Offen', 'Offen'),
};

export const paxFilterFormatterSmallHtml = {
  'Ist-Pax = 0':
    '<span class="badge badge-secondary p-1" style="padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax = 0</span>',
  'Ist-Pax > 0':
    '<span class="badge badge-primary p-1" style="padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax > 0</span>',
  'Ist-Pax = 1':
    '<span class="badge badge-danger p-1" style="background-color: ##ba4753;padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax = 1</span>',
  'Ist-Pax = Max-Pax':
    '<span class="badge badge-secondary p-1" style="color: white; background-color: #159892; padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax = Max-Pax</span>',
  'Ist-Pax < MTN-Pax':
    '<span class="badge p-1 badge-danger" style="padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax &lt; MTN-Pax</span>',
  'Ist-Pax ≥ MTN-Pax':
    '<span class="badge badge-warning p-1" style="padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax ≥ MTN-Pax</span>',
  'Ist-Pax = MTN-1':
    '<span class="badge badge-secondary p-1" style="background-color: #002fff; color: white; padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax = MTN-1</span>',
  'Ist-Pax = MTN-2':
    '<span class="badge badge-secondary p-1" style="background-color: #9f2b68; color: white; padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax = MTN-2</span>',
  'Ist-Pax = Kalk-Pax':
    '<span class="badge badge-secondary p-1" style="background-color: #1BC5BD; color: white; padding: 1px; display: flex; align-items:center; font-size:1rem" >Ist-Pax = Kalk-Pax</span>',
};

export const statusFormatterSmallHtml = {
  InArbeit: statusPillHtml('InArbeit', 'In Arbeit', { small: true }),
  Erledigt: statusPillHtml('Erledigt', 'Erledigt', { small: true }),
  Pending: statusPillHtml('Pending', 'Pending', { small: true }),
  Offen: statusPillHtml('Offen', 'Offen', { small: true }),
};

// Farbzuordnung der Prioritäten: Hoch wie "Offen", Mittel wie "In Arbeit", Niedrig wie "Erledigt"
const prioritaetColors = {
  2: aufgabenStatusColors.Offen,
  1: aufgabenStatusColors.InArbeit,
  0: aufgabenStatusColors.Erledigt,
};

export const prioritaetFormatterHtml = {
  2: pillHtml(prioritaetColors[2], 'Hoch', { widthClass: 'pill-badge--prioritaet' }),
  1: pillHtml(prioritaetColors[1], 'Mittel', { widthClass: 'pill-badge--prioritaet' }),
  0: pillHtml(prioritaetColors[0], 'Niedrig', { widthClass: 'pill-badge--prioritaet' }),
};

export const prioritaetFormatterSmallHtml = {
  2: pillHtml(prioritaetColors[2], 'Hoch', { small: true, widthClass: 'pill-badge--prioritaet' }),
  1: pillHtml(prioritaetColors[1], 'Mittel', { small: true, widthClass: 'pill-badge--prioritaet' }),
  0: pillHtml(prioritaetColors[0], 'Niedrig', { small: true, widthClass: 'pill-badge--prioritaet' }),
};

export const reiseterminStateFormatter = {
  Abgesagt: 'Abgesagt',
  Anlagebereit: 'Anlagebereit',
  Aufgelegt: 'Aufgelegt',
  CmAbgelehnt: 'CM abgelehnt',
  CmAngefragt: 'CM angefragt',
  CmBestaetigt: 'CM bestätigt',
  Durchfuehrung: 'Durchführung',
  Durchgefuehrt: 'Durchgeführt',
  Verworfen: 'Verworfen',
  Vorgeschlagen: 'Vorgeschlagen',
};

// Erzeugt die Pill-Farben aus einer Basisfarbe (Rahmen) und der zugehörigen Textfarbe
function reiseterminStateFarben(basisfarbe, textfarbe) {
  return { background: `${basisfarbe}26`, borderColor: basisfarbe, color: textfarbe };
}

// Farbwerte der Reisetermin-Status-Pills
export const reiseterminStateColors = {
  Abgesagt: reiseterminStateFarben('#F64E60', '#C10007'),
  Anlagebereit: reiseterminStateFarben('#3699FF', '#004A99'),
  Aufgelegt: aufgabenStatusColors.Erledigt,
  CmAbgelehnt: reiseterminStateFarben('#F64E60', '#C10007'),
  CmAngefragt: reiseterminStateFarben('#7337EE', '#3F168F'),
  CmBestaetigt: reiseterminStateFarben('#3699FF', '#004A99'),
  Durchfuehrung: grauPillColors,
  Durchgefuehrt: grauPillColors,
  Verworfen: grauPillColors,
  Vorgeschlagen: grauPillColors,
};

// Verworfene Reisetermine werden abgeschwächt dargestellt
// Ohne feste Breite richtet sich das Badge nach dem Text, z.B. in der Änderungshistorie
export function reiseterminStatePillHtml(state, { small = false, festeBreite = true } = {}) {
  return pillHtml(
    reiseterminStateColors[state] ?? grauPillColors,
    reiseterminStateFormatter[state] ?? state,
    {
      small,
      muted: state === 'Verworfen',
      widthClass: festeBreite ? 'pill-badge--reisetermin-state' : '',
    }
  );
}

// Kompakte Variante für die Filterlisten der Grids
export const reiseterminStateFormatterSmallHtml = Object.fromEntries(
  Object.keys(reiseterminStateFormatter).map(state => [
    state,
    reiseterminStatePillHtml(state, { small: true }),
  ])
);

// Erzeugt das HTML des runden Zusatztermin-Badges
export function zusatzterminBadgeHtml(istZusatztermin) {
  return istZusatztermin ? '<span class="zusatztermin-badge">Z</span>' : '';
}

export function getDate14DaysAgo() {
  const currentDate = new Date();
  const date14DaysAgo = subDays(currentDate, 14);
  return date14DaysAgo;
}

export const normalizeNameForComparison = name => {
  if (!name || typeof name !== 'string') {
    return name;
  }

  // Regex to match honorifics at the end of the string
  const honorificAtEnd = /(?:(?:dr|mr|mrs)\.?\s*)+$/gi;

  let returnedName = name.toLowerCase();

  // Remove honorifics at the end
  returnedName = returnedName.trim().replace(honorificAtEnd, '');

  // Chain all replacements
  return returnedName
    .toLowerCase()
    .replace(/dr\./g, '')
    .replace(/\./g, '')
    .replace(/\s/g, '')
    .replace(/-/g, '')
    .replace(/ö/g, 'oe')
    .replace(/ä/g, 'ae')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .removeDiacritics();
};

String.prototype.removeDiacritics = function () {
  if (!this || this.trim() === '') {
    return this;
  }

  // Normalize to decomposed form (NFD)
  const normalizedString = this.normalize('NFD');

  // Filter out combining diacritical marks
  return normalizedString.replace(/[\u0300-\u036f]/g, '');
};

export function formatSegmentStrecke(segment) {
  const formatDate = date => format(new Date(date.slice(0, -1)), 'dd.MM.yy HH:mm');

  const abflug = `${segment.abflughafen} (${formatDate(segment.abflugsdatum)})`;
  const ankunft = `${segment.zielflughafen} (${formatDate(segment.ankunftsdatum)})`;

  return `${abflug} - ${ankunft}`;
}

function damerauLevenshteinDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create a 2D array (matrix)
  const matrix = [];

  // Initialize first column (0, 1, 2, 3, ...)
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  // Initialize first row (0, 1, 2, 3, ...)
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1.charAt(i - 1) === str2.charAt(j - 1) ? 0 : 1;

      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );

      // Transposition check
      if (
        i > 1 &&
        j > 1 &&
        str1.charAt(i - 1) === str2.charAt(j - 2) &&
        str1.charAt(i - 2) === str2.charAt(j - 1)
      ) {
        matrix[i][j] = Math.min(
          matrix[i][j],
          matrix[i - 2][j - 2] + 1 // transposition
        );
      }
    }
  }

  return matrix[len1][len2];
}

// Calculate similarity percentage
export function levenshteinSimilarity(str1, str2) {
  const distance = damerauLevenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  if (maxLength === 0) return 100; // Both strings are empty

  const similarity = ((maxLength - distance) / maxLength) * 100;
  return similarity;
}

export function formatName(fullName) {
  if (fullName === null || fullName === undefined) {
    return '';
  }
  return fullName
    .split(' ')
    .map(name => name.substring(0, 1).toUpperCase() + name.substring(1).toLowerCase())
    .join(' ')
    .split('-')
    .map(name => name.substring(0, 1).toUpperCase() + name.substring(1))
    .join('-');
}
